/**
 * Best-effort client-side error reporter.
 *
 * Pairs with `/api/client-error/route.ts` (server) and
 * `<ErrorTrap />` + the `error.tsx` boundaries (client surfaces).
 *
 * Why we need this on top of the existing `console.error` calls in
 * the boundaries: the production bundle shows up as
 *   "Error: Minified React error #185; visit https://react.dev/errors/185 ..."
 * which carries no component name, no useful stack, no
 * `componentStack`. The user sees a generic "Something went wrong"
 * card and there is no signal in any server log because the failure
 * is purely on the client. With four replicas behind the edge, we
 * also lose track of *which* replica served the broken render — the
 * shape that has been observed only on takeover.fun (4 replicas) and
 * never on the single-replica testing service.
 *
 * Pipeline:
 *   1. Decode `Minified React error #N` → human-readable message
 *      (we keep a small inline table — only a few errors actually
 *      reach the boundary in practice; #185 is the one we care about
 *      right now).
 *   2. Build a structured payload (path, search, viewport, navigator,
 *      replica id stamped into the document by the layout).
 *   3. POST via `navigator.sendBeacon` if available (survives the
 *      page unload / reset that often follows the error), falling
 *      back to `fetch` with `keepalive: true`.
 *   4. Always swallow our own send errors — never throw from inside
 *      the reporter, the calling site is already in a degraded state.
 */

import {
  getDiagnosticEvents,
  getWalletSnapshot,
  type DiagnosticEvent,
  type WalletSnapshot,
} from "./diagnostic-state";
import {
  getBootPhasesSnapshot,
  type BootPhaseEntry,
} from "./boot-milestones";

const ENDPOINT = "/api/client-error";

const MINIFIED_REACT_ERRORS: Record<string, string> = {
  // Source: react/cjs/react.production.min.js error codes index.
  // We do not need the full table, just the ones the app might hit.
  "185": "Maximum update depth exceeded. This can happen when a component repeatedly calls setState inside componentWillUpdate or componentDidUpdate. React limits the number of nested updates to prevent infinite loops.",
  "418": "Hydration failed because the initial UI does not match what was rendered on the server.",
  "419": "The server could not finish this Suspense boundary, likely due to an error during server rendering. Switched to client rendering.",
  "421": "This Suspense boundary received an update before it finished hydrating. This caused the boundary to switch to client rendering.",
  "422": "There was an error while hydrating this Suspense boundary. Switched to client rendering.",
  "423": "There was an error while hydrating but React was able to recover by instead client rendering the entire root.",
  "425": "Text content does not match server-rendered HTML.",
};

export interface ReportPayload {
  /** Where in the app caught this — `global-error`, `lobby/error`, `window.error`, `console.error`, etc. */
  source: string;
  /** The error itself. `null` only allowed when we only have a message string. */
  error: Error | null;
  /** React component stack, if captured by the `console.error` patch. */
  componentStack?: string | null;
  /** Optional `error.digest` Next.js injected (server-side render errors). */
  digest?: string | null;
  /** Anything else worth including (e.g. the original payload object the boundary logged). */
  extra?: Record<string, unknown>;
}

interface ReplicaMeta {
  ssrReplicaId: string;
  ssrRegion: string;
  ssrDeploymentId: string;
  ssrServiceName: string;
  ssrCommitSha: string;
}

function readReplicaMeta(): ReplicaMeta {
  if (typeof document === "undefined") {
    return {
      ssrReplicaId: "ssr-only-context",
      ssrRegion: "ssr-only-context",
      ssrDeploymentId: "ssr-only-context",
      ssrServiceName: "ssr-only-context",
      ssrCommitSha: "ssr-only-context",
    };
  }
  const get = (name: string): string => {
    const el = document.querySelector(`meta[name="${name}"]`);
    return el?.getAttribute("content") ?? "unknown";
  };
  return {
    ssrReplicaId: get("x-replica-id"),
    ssrRegion: get("x-replica-region"),
    ssrDeploymentId: get("x-deployment-id"),
    ssrServiceName: get("x-service-name"),
    ssrCommitSha: get("x-commit-sha"),
  };
}

/**
 * Decode `Minified React error #N` to a human-readable message.
 * Falls through to the original message when the code isn't in our
 * table (the URL itself still tells you where to look).
 */
export function decodeReactError(message: string | undefined | null): {
  code: string | null;
  decoded: string | null;
} {
  if (!message) return { code: null, decoded: null };
  const match = /Minified React error #(\d+)/.exec(message);
  if (!match) return { code: null, decoded: null };
  const code = match[1];
  return { code, decoded: MINIFIED_REACT_ERRORS[code] ?? null };
}

function safeStringify(value: unknown): string | undefined {
  if (value === undefined) return undefined;
  try {
    return JSON.stringify(value, (_key, v) => {
      if (typeof v === "bigint") return `${v.toString()}n`;
      if (v instanceof Error) {
        return {
          name: v.name,
          message: v.message,
          stack: v.stack,
        };
      }
      return v;
    });
  } catch {
    return undefined;
  }
}

interface DispatchPayload {
  ts: number;
  source: string;
  url: string;
  pathname: string;
  search: string;
  referrer: string;
  userAgent: string;
  viewport: { w: number; h: number; dpr: number } | null;
  online: boolean | null;
  visibility: string | null;
  /** Document-level perf marker so we can spot whether the loop hit on first render or after a long session. */
  uptimeMs: number | null;
  /** SSR replica identity from `<meta>` tags — *not* necessarily the replica that handles this POST. */
  replicaMeta: ReplicaMeta;
  reactError: { code: string | null; decoded: string | null };
  error: {
    name: string;
    message: string;
    stack: string | null;
    digest: string | null;
  };
  componentStack: string | null;
  extra: string | undefined;
  /**
   * Last-known Privy + wagmi state from `<WalletDiagnosticSnapshot />`.
   * Read at error time; values reflect the *last successful render*
   * of the observer, so a tree-toppling throw in the providers does
   * not blank the snapshot — we still get whatever was there a tick
   * earlier. `lastUpdatedAt: null` means the observer never wrote
   * to the singleton (e.g. providers crashed during boot).
   */
  walletSnapshot: WalletSnapshot;
  /**
   * Chronological ring (oldest first) of state-transition events
   * emitted by `<WalletDiagnosticSnapshot />`. Capacity 50; older
   * entries roll off. Truncated further to 30 entries before
   * dispatch to keep payload under the 32KB API cap when a long
   * session has accumulated lots of address/chain swaps.
   */
  diagnosticEvents: DiagnosticEvent[];
  /** First-paint / boot instrumentation for `/` vs `/debug` triage */
  bootPhases: BootPhaseEntry[];
}

function buildPayload(input: ReportPayload): DispatchPayload {
  const err = input.error;
  const url = typeof window !== "undefined" ? window.location.href : "ssr";
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "ssr";
  const search =
    typeof window !== "undefined" ? window.location.search : "";
  const reactError = decodeReactError(err?.message);
  return {
    ts: Date.now(),
    source: input.source,
    url,
    pathname,
    search,
    referrer:
      typeof document !== "undefined" ? document.referrer ?? "" : "",
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : "ssr",
    viewport:
      typeof window !== "undefined"
        ? {
            w: window.innerWidth,
            h: window.innerHeight,
            dpr: window.devicePixelRatio ?? 1,
          }
        : null,
    online:
      typeof navigator !== "undefined" ? navigator.onLine : null,
    visibility:
      typeof document !== "undefined" ? document.visibilityState : null,
    uptimeMs:
      typeof performance !== "undefined" ? Math.round(performance.now()) : null,
    replicaMeta: readReplicaMeta(),
    reactError,
    error: {
      name: err?.name ?? "Error",
      message: err?.message ?? "(no message)",
      stack: err?.stack ?? null,
      digest: input.digest ?? null,
    },
    componentStack: input.componentStack ?? null,
    extra: safeStringify(input.extra),
    walletSnapshot: getWalletSnapshot(),
    // Cap the events at the most recent 30 — covers the relevant
    // post-connect window for the #185 reproduction without
    // bloating the payload past the 32KB API cap.
    diagnosticEvents: getDiagnosticEvents().slice(-30),
    bootPhases: getBootPhasesSnapshot().slice(-25),
  };
}

/**
 * Module-level dedupe so React 18 strict-mode double-invocation, the
 * console.error patch, and the boundary's own useEffect don't all
 * report the same error three times. Keys are
 *   `${source}|${error.name}|${error.message}|${stack-first-200ch}`
 * which ignores ts/url so the same loop reported from `console.error`
 * vs `global-error-boundary` still dedupes (we keep the first to
 * arrive, which is usually the console.error patch — it has the
 * `componentStack`).
 *
 * The window is short (5s) because we *do* want to capture
 * subsequent re-occurrences (different reproductions, different
 * users) — just not the same error fired multiple times in the same
 * tick.
 */
const SENT_RECENTLY = new Map<string, number>();
const DEDUPE_WINDOW_MS = 5_000;

function fingerprint(payload: DispatchPayload): string {
  const stackHead = (payload.error.stack ?? "").slice(0, 200);
  return [
    payload.source,
    payload.error.name,
    payload.error.message,
    stackHead,
  ].join("|");
}

function dispatch(body: string): void {
  if (typeof window === "undefined") return;
  // sendBeacon is the most reliable channel during teardown
  // (page is being torn down by the error boundary, navigation is
  // about to happen, a `reset()` is queued, etc.). It uses a small
  // POST with `Content-Type: text/plain` semantics — our route reads
  // the raw body so the content type doesn't matter for parsing.
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    } catch {
      // fall through to fetch
    }
  }
  // `keepalive: true` lets the request outlive the page if the
  // boundary's `reset()` triggers a soft navigation.
  try {
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      // Don't await — this is fire-and-forget.
    }).catch(() => {});
  } catch {
    // never throw from the reporter
  }
}

export function reportClientError(input: ReportPayload): void {
  if (typeof window === "undefined") return;
  let payload: DispatchPayload;
  try {
    payload = buildPayload(input);
  } catch {
    // If even building the payload throws, we still want a minimal
    // signal in the server log.
    payload = {
      ts: Date.now(),
      source: input.source,
      url: "(payload-build-failed)",
      pathname: "",
      search: "",
      referrer: "",
      userAgent: "",
      viewport: null,
      online: null,
      visibility: null,
      uptimeMs: null,
      replicaMeta: {
        ssrReplicaId: "unknown",
        ssrRegion: "unknown",
        ssrDeploymentId: "unknown",
        ssrServiceName: "unknown",
        ssrCommitSha: "unknown",
      },
      reactError: { code: null, decoded: null },
      error: {
        name: "ReporterPayloadBuildError",
        message: "Failed to build error payload",
        stack: null,
        digest: null,
      },
      componentStack: null,
      extra: undefined,
      walletSnapshot: getWalletSnapshot(),
      diagnosticEvents: getDiagnosticEvents().slice(-30),
      bootPhases: getBootPhasesSnapshot().slice(-25),
    };
  }

  const fp = fingerprint(payload);
  const now = Date.now();
  const last = SENT_RECENTLY.get(fp);
  if (last && now - last < DEDUPE_WINDOW_MS) return;
  SENT_RECENTLY.set(fp, now);
  // Garbage-collect old entries so the map doesn't grow unbounded
  // during a long debug session on a hot loop.
  if (SENT_RECENTLY.size > 64) {
    for (const [k, ts] of SENT_RECENTLY) {
      if (now - ts > DEDUPE_WINDOW_MS) SENT_RECENTLY.delete(k);
    }
  }

  let body: string;
  try {
    body = JSON.stringify(payload);
  } catch {
    body = JSON.stringify({
      ts: now,
      source: input.source,
      error: { name: "Unserializable", message: "Payload not JSON-safe" },
    });
  }

  // Browser-visible mirror of the same payload that goes to the
  // server. Without this the user has to flip between Railway logs
  // and DevTools to see anything beyond the bare React message —
  // and the most useful new fields (`componentStack`,
  // `walletSnapshot`, `diagnosticEvents`) only ever existed on the
  // server side. Logged via `console.warn` instead of `console.error`
  // so it doesn't recursively trigger the boundary forwarder
  // installed by `<ErrorTrap />`. The full payload object is logged
  // (not stringified) so DevTools renders it as an interactive
  // Object the user can expand. Trimmed `stack` and
  // `componentStack` keep the line readable in the Network tab if
  // someone copies the line.
  try {
    // eslint-disable-next-line no-console
    console.warn(
      `[client-error/local] ${payload.source} :: ${
        payload.reactError.code
          ? `React #${payload.reactError.code}`
          : payload.error.name
      } @ ${payload.pathname}`,
      payload,
    );
  } catch {
    // never throw from the reporter
  }

  dispatch(body);
}
